import React from 'react';
import { Lock } from 'lucide-react';
import OnboardingPortal from '../OnboardingPortal';
import { isAdminUser, getUserEmail } from '../../lib/admin';

export interface AuthOverlayProps {
  isDevAdminRoute: boolean;
  setShowGodPanel: (val: boolean) => void;
  isAuthLoading: boolean;
  user: any;
  isSubscriptionActive: boolean;
  setIsSubscriptionActive: (val: boolean) => void;
  authStatus: string;
  authToken: string;
  setAuthToken: (val: string) => void;
  authError: string;
  setAuthError: (val: string) => void;
  setAuthStatus: (val: any) => void;
  handleLogin: (token: string, force?: boolean) => void;
  showLoginLogoutConfirm: boolean;
  setShowLoginLogoutConfirm: (val: boolean) => void;
  handleLogout: () => void;
}

export const AuthOverlay: React.FC<AuthOverlayProps> = ({
  isDevAdminRoute,
  setShowGodPanel,
  isAuthLoading,
  user,
  isSubscriptionActive,
  setIsSubscriptionActive,
  authStatus,
  authToken,
  setAuthToken,
  authError,
  setAuthError,
  setAuthStatus,
  handleLogin,
  showLoginLogoutConfirm,
  setShowLoginLogoutConfirm,
  handleLogout,
}) => {
  const isAdmin = isAdminUser(user);

  if (isDevAdminRoute && !isAdmin) {
    return (
      <div className="w-screen h-[100dvh] bg-[#02040a] flex items-center justify-center font-mono text-[#e0e0e0]">
        <div className="bg-[#060919] p-8 rounded-xl border border-[#00d9ff]/20 w-full max-w-md shadow-[0_0_40px_rgba(0,217,255,0.1)]">
          <h2 className="text-[#00d9ff] text-[16px] uppercase tracking-[3px] font-bold mb-6 text-center">FLOWAES DEV-ADMIN</h2>
          <div className="space-y-4">
            <a
              href="/"
              className="block w-full bg-black/40 border border-[#00d9ff]/20 rounded px-4 py-3 text-[12px] text-[#00d9ff] hover:border-[#00d9ff] text-center tracking-widest uppercase"
            >
              Entrar com a conta de administrador
            </a>
            <p className="text-[#666] text-[10px] uppercase text-center tracking-[1px] leading-relaxed">
              Acesso restrito à equipe de desenvolvimento e arquitetos do sistema.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (isAuthLoading && !isAdmin) {
    return (
      <div className="w-screen h-[100dvh] bg-[#02040a] flex items-center justify-center font-mono text-[#e0e0e0]">
        <div className="flex flex-col items-center gap-4">
           <div className="w-8 h-8 rounded-full border-t-2 border-[#00d9ff] animate-spin"></div>
           <p className="text-[10px] uppercase tracking-widest text-[#00d9ff]/70">Inicializando Núcleo Quântico...</p>
        </div>
      </div>
    );
  }

  const showOnboardingPortal = !isAdmin && (!user || !isSubscriptionActive);

  if (showOnboardingPortal) {
    return (
      <div className="w-screen h-[100dvh] bg-[#02040a] relative font-mono text-[#e0e0e0] overflow-hidden">
        <OnboardingPortal onAccessGranted={() => setIsSubscriptionActive(true)} />
      </div>
    );
  }

  if (authStatus !== "authenticated" && authStatus !== "reconnecting" && !isAdmin) {
    return (
      <div className="w-screen h-[100dvh] bg-[#02040a] flex flex-col items-center justify-center p-4 select-none">
        <div className="max-w-md w-full bg-[#0a0e27]/80 backdrop-blur-xl p-8 rounded-xl border border-[#00d9ff]/30 shadow-[0_10px_40px_rgba(0,0,0,0.8)] relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-[#00ffa3] to-[#ff3d67]" />
          <h1 className="text-3xl font-mono text-white text-center mb-2 tracking-[8px] bg-gradient-to-r from-[#00d9ff] to-[#0099ff] bg-clip-text text-transparent">FLOWAES</h1>
          <p className="text-[10px] uppercase tracking-widest text-center text-[#888888] mb-2">Acesso Exclusivo de Fundador</p>
          <p className="text-[10px] text-center text-[#555555] font-mono mb-8 break-all">
            Conta conectada: {getUserEmail(user) || 'sem e-mail'}
          </p>
          
          <form onSubmit={(e) => { e.preventDefault(); handleLogin(authToken); }} className="space-y-6">
            <div>
              <label className="block text-[10px] uppercase font-bold tracking-[3px] text-[#00d9ff] mb-3">Token de Licença Ativa</label>
              <input 
                type="text" 
                value={authToken}
                onChange={e => {
                  setAuthToken(e.target.value.toUpperCase());
                  if (authError) setAuthError("");
                  if (authStatus === "error" || authStatus === "locked") {
                    setAuthStatus("idle");
                  }
                }}
                placeholder="Insira sua Licença"
                className="w-full bg-[#050714] border border-[#333333] rounded px-4 py-4 text-[#ffffff] font-mono text-sm tracking-widest focus:border-[#00d9ff] focus:ring-1 focus:ring-[#00d9ff] focus:outline-none transition-all placeholder:text-[#333333]"
                disabled={authStatus === "authenticating"}
              />
            </div>
            
            {authError && (
              <div className="p-4 bg-[#ff3d67]/10 border border-[#ff3d67]/30 rounded text-[#ff3d67] text-xs font-mono tracking-wider animate-pulse">
                {authError}
              </div>
            )}
            
            {authStatus === "locked" ? (
              <button 
                type="button" 
                onClick={() => handleLogin(authToken, true)}
                className="w-full bg-[#ff3d67]/10 border border-[#ff3d67]/50 text-[#ff3d67] hover:bg-[#ff3d67]/20 font-bold uppercase tracking-[4px] text-xs py-4 rounded transition-all shadow-[0_0_15px_rgba(255,61,103,0.15)] hover:shadow-[0_0_20px_rgba(255,61,103,0.3)]"
              >
                Forçar Entrada e Desconectar
              </button>
            ) : (
              <button 
                type="submit" 
                disabled={authStatus === "authenticating" || !authToken || authToken.length < 5}
                className="w-full bg-[#00d9ff]/10 border border-[#00d9ff]/50 text-[#00d9ff] hover:bg-[#00d9ff]/20 font-bold uppercase tracking-[4px] text-xs py-4 rounded transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_0_15px_rgba(0,217,255,0.15)] hover:shadow-[0_0_20px_rgba(0,217,255,0.3)]"
              >
                {authStatus === "authenticating" ? "Conectando ao Nó Seguro..." : "Validar Licença"}
              </button>
            )}
          </form>

          {showLoginLogoutConfirm ? (
            <div className="mt-4 p-4 border border-[#ff3d67]/30 bg-[#0a0e27]/90 rounded-xl space-y-3 animate-in fade-in zoom-in duration-200">
              <p className="text-[#ff3d67] text-[10px] text-center font-bold uppercase tracking-widest">
                Tem certeza que deseja sair?
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setShowLoginLogoutConfirm(false)}
                  className="flex-1 py-2 text-[10px] font-bold uppercase tracking-widest text-[#666] border border-[#666]/30 hover:bg-white/5 rounded transition-all"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleLogout}
                  className="flex-1 py-2 text-[10px] font-bold uppercase tracking-widest text-white bg-[#ff3d67] hover:bg-[#ff3d67]/80 shadow-[0_0_15px_rgba(255,61,103,0.3)] rounded transition-all"
                >
                  Sim, Sair
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setShowLoginLogoutConfirm(true)}
              className="w-full bg-[#ff3d67]/10 border border-[#ff3d67]/30 hover:bg-[#ff3d67]/20 text-[#ff3d67] font-bold uppercase tracking-[3px] text-xs py-3 rounded transition-all mt-4"
            >
              Sair da Conta / Trocar de Conta
            </button>
          )}
          
          <div className="mt-8 pt-6 border-t border-[#00d9ff]/10 flex items-center justify-center space-x-3 text-[10px] font-mono text-[#555555]">
            <Lock size={12} className="text-[#00ffa3]" />
            <span className="uppercase tracking-widest">Sessão Única Criptografada</span>
          </div>
        </div>
      </div>
    );
  }

  return null;
};
